import React, { useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';
import { Common } from '@thecvlb/design-system';
import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';

import { useGetSignableDocumentsQuery, useSignDocumentMutation } from 'services/general/general';

import { selectOrchestrate } from 'store';

import { ReducedSignupStepProps } from 'containers/SignUp/Content/content.types';
import FadeWrapper from 'shared/animationWrappers/FadeWrapper';
import Loader from 'shared/Loader';

import { useAppSelector } from 'hooks';

const Terms: React.FC<ReducedSignupStepProps & { targetName: string }> = ({
  moveToStep,
  targetName
}) => {
  const {
    user: { accessToken }
  } = useAppSelector(selectOrchestrate);
  const { data, isLoading } = useGetSignableDocumentsQuery({ accessToken });
  const [signDocument, { isLoading: isSigningDocument }] = useSignDocumentMutation();
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isRead, toggleRead] = useState(false);

  const endOfAgreement = useRef<HTMLSpanElement>(null);

  const intersection = useIntersection(endOfAgreement as React.RefObject<HTMLElement>, {
    root: null,
    rootMargin: '0px',
    threshold: 1
  });

  const { documentId: templateId, html } = data?.data.find(
    (item) => item.type === 'insurance-terms'
  ) || { documentId: '', html: '' };

  const handleContinue = () => {
    if (!document) return;
    signDocument({ accessToken, templateId })
      .unwrap()
      .then(() => moveToStep('next'));
  };

  useEffect(() => {
    if (intersection?.isIntersecting) {
      toggleRead(true);
    }
  }, [intersection?.isIntersecting]);

  return (
    <FadeWrapper className="flex flex-col gap-4">
      <Loader isVisible={isLoading} />
      <h1 className="text-2xl font-bold" data-testid="header">
        Terms, Privacy Policy and Consent to Telehealth
      </h1>
      <Common.Alert type="info" colorableBackground>
        Please scroll down to continue.
      </Common.Alert>
      <div className="max-h-[300px] overflow-auto rounded-xl border bg-gray-100 p-3 text-sm text-gray-600">
        {HTMLReactParser(html)}
        <span ref={endOfAgreement}></span>
      </div>
      <div className="flex flex-col gap-2 rounded-xl bg-gray-100 p-4">
        <p className={classNames('text-sm', isFocused ? 'text-primary' : 'text-gray')}>
          By signing this box, you are agreeing to the above statements.{' '}
          <b>Please sign your full name as you previously entered on the form.</b>
        </p>
        <Common.Input
          dataTestId="full_name_input"
          placeholder="Full name"
          value={inputValue}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
        />
        <p className="select-none">
          {targetName.split('').map((char, index) => (
            <span
              className={
                targetName.includes(inputValue)
                  ? inputValue[index]
                    ? inputValue[index] === char
                      ? 'font-bold text-green'
                      : 'text-red'
                    : ''
                  : 'text-red'
              }
              key={index}
            >
              {char}
            </span>
          ))}
        </p>
      </div>
      <Common.Button
        className="mx-auto"
        color="blue"
        dataTestId="continue_btn"
        disabled={targetName !== inputValue || !isRead || isLoading}
        isLoading={isSigningDocument}
        onClick={handleContinue}
      >
        Continue
      </Common.Button>
    </FadeWrapper>
  );
};

export default Terms;
